import {FieldError, get, Path, useFormContext} from "react-hook-form";
import Text from "./Text";
import Institute from "./Institute";
import {EMAIL_REGEX} from "../../../../researcher/new-researcher/NewResearcherConstants";
import {InstituteSelection} from "../../../util/InstituteUtils";

/**
 * Props for this component
 */
interface ResearcherInstituteProps<T extends Record<string, any>> {
  label: string;
  fieldId: Path<T>;
  disabled?: boolean;
  primary?: boolean;
  defaultInstitute?: InstituteSelection;
  deleteInstitute?: () => void;
}

/**
 * This will add a panel to allow the user to input the information related to a researcher institute. It is a more
 * complex component that includes an institute select input alongside some text input.
 *
 * @param label The text to show before the text box. (By default this will show to the left of the input
 * @param fieldId The ID of the field as defined in the form interface
 * @param disabled <i>Optional</i> A boolean to mark the field as disabled
 * @param primary <i>Optional</i> Marks this researcher institute as being for the primary institute
 * @param defaultInstitute <i>Optional</i> Passes a default value
 * @param deleteInstitute <i>Optional</i> A function that can be used to delete this project institute entry
 */
export default function ResearcherInstitute<T extends Record<string, any>>({
                                                                             label,
                                                                             fieldId,
                                                                             disabled = false,
                                                                             primary = false,
                                                                             defaultInstitute,
                                                                             deleteInstitute
                                                                           }: ResearcherInstituteProps<T>) {
  const {formState: {errors}} = useFormContext<T>();

  // Work out what the paths for the specific form elements are
  const emailPath = `${fieldId}.email` as Path<T>
  const positionPath = `${fieldId}.position` as Path<T>
  const departmentPath = `${fieldId}.department` as Path<T>;

  // Find errors
  const emailError = get(errors, emailPath) as FieldError | undefined;
  const positionError = get(errors, positionPath) as FieldError | undefined;
  const departmentError = get(errors, departmentPath) as FieldError | undefined;

  return <div className={"mb-2"}>
    <Institute<T>
        label={label}
        fieldId={fieldId as Path<T>}
        helpText={primary && (
            !defaultInstitute ?
                "This will be the institute under which your application will be submitted." :
                undefined)}
        deleteInstitute={deleteInstitute}
        mandatory
        disabled={disabled || defaultInstitute !== undefined}
        primary={primary}
        defaultValue={defaultInstitute}
        allowNew/>
    <Text<T>
        label={"Email at this Institute"}
        error={emailError}
        fieldId={emailPath}
        disabled={disabled || defaultInstitute !== undefined}
        mandatory
        pattern={EMAIL_REGEX}/>
    <Text<T>
        label={"Position"}
        error={positionError}
        fieldId={positionPath}
        disabled={disabled}
        mandatory/>
    <Text<T>
        label={"Department"}
        error={departmentError}
        disabled={disabled}
        fieldId={departmentPath}/>
  </div>
}
